/* eslint-disable enforce-ids-in-jsx/missing-ids */
import React from 'react'

import { formateDesignSystemDate } from 'utils/dateUtils'

import Checkbox from 'components/lib/DesignSystem/Checkbox'
import { FillCrownPsima, SerialClicker, Untrained } from 'components/lib/DesignSystem/Icons'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'
import { PSIMA_PENDO_FEATURES } from 'components/pages/SpearPhishingAgent/psimaPendo'

import {
  NameText,
  NameTooltipContent,
  NameTooltipEmail,
  NameTooltipName,
  NameTooltipPill,
  NameTooltipPills,
  NameWithSignals,
  RiskSignalChip,
  TableCheckboxContainer,
  TableNameContainer,
  VerifiedRowTrigger,
} from '../styles'

import AgentVerifiedMark from './AgentVerifiedMark'

const getRiskSignals = ({ isVip, isSerialClicker, isUntrained }) =>
  [
    isVip && { key: 'vip', variant: 'vip', label: 'VIP', Icon: FillCrownPsima },
    isSerialClicker && {
      key: 'serialClicker',
      variant: 'serialClicker',
      label: 'Serial clicker',
      Icon: SerialClicker,
    },
    isUntrained && {
      key: 'untrained',
      variant: 'untrained',
      label: 'Untrained',
      Icon: Untrained,
    },
  ].filter(Boolean)

const getParticipantColumns = ({
  campaignId,
  participantIds,
  excludedParticipantIds,
  setExcludedParticipant,
  toggleAllParticipants,
  getParticipantDuplicateState,
}) => {
  const isAllSelected = participantIds.length > 0 && excludedParticipantIds.length === 0
  const isIndeterminate =
    excludedParticipantIds.length > 0 && excludedParticipantIds.length < participantIds.length

  return [
    {
      key: 'checkbox',
      width: '48px',
      header: (
        <TableCheckboxContainer>
          <Checkbox
            id={`Psima_Dashboard_ParticipantSelectAll_${campaignId}`}
            dataTestId={`Psima_Dashboard_ParticipantSelectAll_${campaignId}`}
            dataPendoFeature={PSIMA_PENDO_FEATURES.DASHBOARD_PARTICIPANT_TOGGLE}
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={(e) => {
              e.stopPropagation()
              toggleAllParticipants(campaignId, participantIds)
            }}
          />
        </TableCheckboxContainer>
      ),
      render: ({ id }) => (
        <TableCheckboxContainer>
          <Checkbox
            id={`Psima_Dashboard_ParticipantCheckbox_${campaignId}_${id}`}
            dataTestId={`Psima_Dashboard_ParticipantCheckbox_${campaignId}_${id}`}
            dataPendoFeature={PSIMA_PENDO_FEATURES.DASHBOARD_PARTICIPANT_TOGGLE}
            checked={!excludedParticipantIds.includes(id)}
            onChange={(e) => {
              e.stopPropagation()
              setExcludedParticipant(campaignId, id)
            }}
          />
        </TableCheckboxContainer>
      ),
    },
    {
      key: 'employee',
      header: 'Employee',
      render: ({ id, name, email, isVip, isSerialClicker, isUntrained }) => {
        const isReassigned = getParticipantDuplicateState(campaignId, id) === 'loser'
        const signals = getRiskSignals({ isVip, isSerialClicker, isUntrained })

        return (
          <TableNameContainer>
            <Tooltip
              id={`Psima_Dashboard_EmployeeIdentity_tooltip-${id}`}
              side="top"
              arrow
              content={
                <NameTooltipContent>
                  <NameTooltipName>{name}</NameTooltipName>
                  <NameTooltipEmail>{email}</NameTooltipEmail>
                  {signals.length > 0 && (
                    <NameTooltipPills>
                      {signals.map(({ key, variant, label, Icon }) => (
                        <NameTooltipPill key={key} $variant={variant}>
                          <Icon width="12px" height="12px" fill="currentColor" />
                          {label}
                        </NameTooltipPill>
                      ))}
                    </NameTooltipPills>
                  )}
                </NameTooltipContent>
              }
            >
              <NameWithSignals $dimmed={isReassigned}>
                <NameText data-testid={`Psima_Dashboard_EmployeeName_div-${id}`}>{name}</NameText>
                {signals.map(({ key, variant, label, Icon }) => (
                  <RiskSignalChip
                    key={key}
                    $variant={variant}
                    title={label}
                    data-testid={`Psima_Dashboard_Employee_${key}_chip-${id}`}
                  >
                    <Icon width="13px" height="13px" fill="currentColor" />
                  </RiskSignalChip>
                ))}
              </NameWithSignals>
            </Tooltip>
            {isReassigned && (
              <Tooltip
                id={`Psima_Dashboard_VerifiedByAgent_tooltip-${id}`}
                side="top"
                arrow
                content="The Agent placed this employee in a better-fit simulation to avoid overlap. Click to include them here instead."
              >
                <VerifiedRowTrigger
                  data-testid={`Psima_Dashboard_VerifiedByAgent_button-${id}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setExcludedParticipant(campaignId, id)
                  }}
                >
                  <AgentVerifiedMark variant="row" />
                </VerifiedRowTrigger>
              </Tooltip>
            )}
          </TableNameContainer>
        )
      },
    },
    { key: 'department', header: 'Department' },
    { key: 'jobTitle', header: 'Role' },
    {
      key: 'lastSimulationTime',
      header: 'Last Simulated',
      render: ({ lastSimulationTime }) => (
        <div>
          {lastSimulationTime
            ? formateDesignSystemDate(new Date(lastSimulationTime), {
                withYear: true,
                monthFirst: true,
                withMinutes: true,
              })
            : '-'}
        </div>
      ),
    },
  ]
}

export default getParticipantColumns
